import { useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getDocument, getSubDocument, queryATTOM, cloudFunctionV2, propertyGatewayString, propertyGatewayStringReRun, sendErrorEmail, regenerateTimeline, startCompSelectionAgent } from '../functions';
import { defaultSettings } from '../defaults';

/**
 * Custom hook for managing property data fetching and Firestore operations
 * @param {Object} params - Configuration object
 * @param {string} params.propertyId - Property ID from URL params
 * @param {string} params.countyId - County ID from URL params
 * @param {Object} params.db - Firestore database instance
 * @param {Object} params.refs - Refs object containing firestoreUnsubscribeRef, arvMessagesLengthRef, conditionFilterOptionsRef
 * @param {Object} params.stateSetters - Object containing all state setter functions
 * @returns {Object} Object containing fetchPropertyData function and other utilities
 */
export const usePropertyData = ({
    propertyId,
    countyId,
    db,
    refs,
    stateSetters,
    skipAgent = false
}) => {
    const {
        firestoreUnsubscribeRef,
        arvMessagesLengthRef,
        conditionFilterOptionsRef
    } = refs;

    const {
        setConditionFilterOptions,
        setSelectedComps,
        setRentReadyData,
        setAsIsData,
        setStreetViewAnalysis,
        setArvMessages,
        setTypeOutText,
        setRedfinData,
        setArvRequested,
        setArvEstimate,
        setArvRange,
        setRiskSpectrum,
        setArvModeActive,
        setArvReasoning,
        setPurchasePrice,
        setSubdivisions,
        setConditionUpdate,
        setImageRoomLabels,
        setCurbAppeals,
        setCeilings,
        setFloorZPID,
        setCeilingZPID,
        setAssumptions,
        setBasementAnalysis,
        setFloodZoneAnalysis,
        setFullSubdivisionAnalysis,
        setRedfinMarketTrends,
        setConstructionMaterialAnalysis,
        setSubjectPropertyAnalysis,
        setAnalysisArrivalOrder,
        setRepairCostsData,
        setRepairCostsFetched,
        repairCostsFetchedRef,
        setAllComps,
        setFilteredComps,
        setProperty,
        setLoading,
        setErrorMessage,
        setNetworkError,
        setAdu,
        setPool,
        setGarage,
        setGarageSpaces,
        setGarageType,
        setCarport,
        setCarportSpaces,
        setBasement,
        setMinLotSize,
        setRemodelLevel,
        setPropertyNotes,
        setAnomaly,
        setArvWorkflow,
        setPropertyEstimates,
        setSourceURL,
        setSource,
        setBasementSqFootage,
        setViewRepairCosts,
        setArvAgentMarketTemp,
        setArvAgentMarkets,
        setArvAgentPhase,
        setCrossMarketBounds,
        setArvConfidence,
        setCompsAnalysis,
        setArvAgentCrossMarketWidening,
        setArvAgentInvestigationSummary,
        setArvJourney,
        setArvPhases,
        setLastRunAt,
        setCompProblemCodes,
        setCompScoreDetails,
        setCompFetcherFilterReport
    } = stateSetters;

    const getMLSData = useCallback(async(savedCompanyId, props) => {
        const getCompanyData = await getDocument("companies", savedCompanyId);
        if ( getCompanyData.status === 200 ) {
            const data = getCompanyData.data.data;
            if ( data.mlsData !== true && data.attom !== true ) {
                return props;
            }
            else if ( data.mlsData === true ) {
                for (let index = 0; index < props.length; index++) {
                    const element = props[index];
                    if ( element.addressDetails.state === "FL" ) {
                        break;
                    }
                    else if ( element.listingDetails.listingStatus === "for_sale" || element.listingDetails.listingStatus === "pending" ) {
                        continue;
                    }
                    const colRef = "mls_data";
                    const subColRef = "properties";
                    const propertyId = element.uniqueIds?.reapiId;
                    if (!propertyId) {
                        continue;
                    }
                    const soldListingDate = props[index].listingDetails.soldListingDate;
                    const soldListingDateTS = new Date(soldListingDate);
                    const soldListingDateTSSeconds = Math.floor(soldListingDateTS.getTime() / 1000);

                    const queryData = await getSubDocument(colRef, savedCompanyId, subColRef, propertyId);
                    if ( queryData.status === 200 ) {
                        const price = queryData.data.price;
                        const expiryDate = queryData.data.date;
                        const expiryDateSeconds = expiryDate.seconds
                        if ( soldListingDateTSSeconds < expiryDateSeconds ) {
                            props[index].mlsVerified = true;
                            props[index].listingDetails.soldListingPrice = price;
                        }
                    }
                }
                return props;
            }
            else if ( data.attom === true ) {
                for (let index = 0; index < props.length; index++) {
                    const element = props[index];
                    if ( element.addressDetails.state === "FL" ) {
                        break;
                    }
                    else if ( element.listingDetails.listingStatus === "for_sale" || element.listingDetails.listingStatus === "pending" ) {
                        continue;
                    }
                    
                    const queryData = await queryATTOM(element.addressDetails.streetAddress, element.addressDetails.city, element.addressDetails.state, element.addressDetails.postalCode);
                    if ( queryData.status === 200 ) {
                        const amount = queryData.amount;
                        if ( amount === undefined ) {
                            continue;
                        }
                        else {
                            props[index].mlsVerified = true;
                            props[index].listingDetails.soldListingPrice = amount;
                        }
                    }
                    else {
                        continue;
                    }
                }
                return props;
            }
            else {
                return props;
            }
        }
        else {
            return props;
        }
    }, []);

    const fetchSettings = useCallback(async(newCompanyId) => {
        const colRef = "settings";
        const queryData = await getDocument(colRef, newCompanyId);
        if ( queryData.status === 200 ) {
            const data = queryData.data.data;
            return data;
        }
        else {
            return defaultSettings;
        }
    }, []);

    const pickSelectedCompConditions = useCallback((newSelectedComps, newAllComps) => {
        const selectedConditions = [];
        for (let index = 0; index < newSelectedComps.length; index++) {
            const element = newSelectedComps[index];
            const zpid = element.zpid;
            const comp = newAllComps.find(e => e.uniqueIds.zillowId === zpid);
            if (comp) {
                const condition = comp.compDetails.propertyCondition;
                const conditionIndex = selectedConditions.indexOf(condition);
                if (condition !== "Unclear" && conditionIndex === -1) {
                    selectedConditions.push(condition);
                }
            }
        };
        const clone = [...conditionFilterOptionsRef.current];
        for (let index = 0; index < selectedConditions.length; index++) {
            const element = selectedConditions[index];
            const cloneIndex = clone.findIndex(e => e.label === element);
            if (cloneIndex !== -1) {
                clone[cloneIndex].selected = true;
            }
        }
        // Always ensure "Recently Remodeled" is selected
        const recentlyRemodeledIndex = clone.findIndex(e => e.label === "Recently Remodeled");
        if (recentlyRemodeledIndex !== -1) {
            clone[recentlyRemodeledIndex].selected = true;
        }
        setConditionFilterOptions(clone);
    }, [conditionFilterOptionsRef, setConditionFilterOptions]);

    const queryBestComps = useCallback(async (newProperty, newAllComps, newCompanyId, newPropertyNotes, newADU, newPool, newGarage, newGarageSpaces, newGarageType, newRemodelLevel, newFirstName, refreshSubjectAnalysis = false) => {
        // Firestore listener setup - cleanup is handled by useEffect return function
        const ref = doc(db, 'companies', newCompanyId, 'properties', newProperty.uniqueIds.propertyId);
        const unsubscribe = onSnapshot(ref, async snap => {
            const data = snap.data()?.data;
            if (!data) return;

            if (data.selectedComps && data.selectedComps.length > 0) {
                setSelectedComps(data.selectedComps);
                pickSelectedCompConditions(data.selectedComps, newAllComps);
            }

            // Surface the two peer comp roles arvAgent writes alongside ARV
            // (well-maintained + moderate-rehab tiers) — RunCompsV2 reads these
            // to drive the role-fork tiles on CompTaxonomyTree. Optional-chained
            // so callers that don't supply the setters are unaffected.
            if (data.rentReadyData !== undefined) {
                setRentReadyData?.(data.rentReadyData || null);
            }
            if (data.asIsData !== undefined) {
                setAsIsData?.(data.asIsData || null);
            }

            if (data.streetViewAnalysis !== undefined) {
                setStreetViewAnalysis(data.streetViewAnalysis);
            }
            
            if (data.arvMessages && data.arvMessages.length !== arvMessagesLengthRef.current) {
                setArvMessages(data.arvMessages);
                setTypeOutText(true);
            }

            if (data.redfinData) {
                setRedfinData(data.redfinData);
            }

            // Update ARV values if arvData changes
            if (data.arvData) {
                if ( data.arvRequested === false ) {
                    // Don't show ARV yet as they haven't requested it yet
                    setArvRequested(false);
                }
                else if (data.arvData.arv !== undefined && data.arvData.arv !== null) {
                    setArvEstimate(data.arvData.arv);
                    setArvRange(data.arvData.arvRange);
                    if ( data.arvData.riskSpectrum !== undefined && data.arvData.riskSpectrum !== null ) {
                        setRiskSpectrum(data.arvData.riskSpectrum);
                    }
                    setArvModeActive(true);
                    setArvRequested(true);
                    if (data.arvData.reasoning) {
                        setArvReasoning(data.arvData.reasoning);
                    }
                    if (data.arvData.crossMarketBounds && setCrossMarketBounds) {
                        setCrossMarketBounds(data.arvData.crossMarketBounds);
                    }
                    if (data.arvData.confidence && setArvConfidence) {
                        setArvConfidence(data.arvData.confidence);
                    }
                    if (data.arvData.crossMarketWidening && setArvAgentCrossMarketWidening) {
                        setArvAgentCrossMarketWidening(data.arvData.crossMarketWidening);
                    }
                    if (data.arvData.investigationSummary && setArvAgentInvestigationSummary) {
                        setArvAgentInvestigationSummary(data.arvData.investigationSummary);
                    }
                    if (data.arvData.arvJourney && setArvJourney) {
                        setArvJourney(data.arvData.arvJourney);
                    }
                    if (data.arvData.deterministicArv?.phases && setArvPhases) {
                        // Phase 4 only carries summary fields in the phases block.
                        // The full per-comp audit (doubtPool, doubtImplied, etc.)
                        // lives at arvData.doubtLayer — merge it in so the UI can
                        // surface which comp triggered the doubt pull.
                        const phases = data.arvData.deterministicArv.phases;
                        const doubt = data.arvData.doubtLayer;
                        // Spread doubt first, phases.phase4_doubtLayer second so
                        // phases-shape fields (e.g. ignoredNoTenure as count, not
                        // array) win — the audit just adds new fields like
                        // doubtPool, doubtImplied, soldStrength, doubtStrength.
                        const enriched = doubt
                            ? { ...phases, phase4_doubtLayer: { ...doubt, ...(phases.phase4_doubtLayer || {}) } }
                            : phases;
                        setArvPhases(enriched);
                    }
                    if (data.arvData.compProblemCodes && setCompProblemCodes) {
                        setCompProblemCodes(data.arvData.compProblemCodes);
                    }
                }
            }
            if (data.arvAgentCompScoreDetails && setCompScoreDetails) {
                setCompScoreDetails(data.arvAgentCompScoreDetails);
            }
            if (data.compsAnalysis && setCompsAnalysis) {
                setCompsAnalysis(data.compsAnalysis);
            }

            if ( data.purchasePrice ) {
                setPurchasePrice(data.purchasePrice);
            }

            if (data.subdivisions) {
                setSubdivisions(data.subdivisions);
            }
            
            if (data.conditionUpdate) {
                setConditionUpdate(data.conditionUpdate);
            }

            if (data.imageRoomLabels) {
                setImageRoomLabels(data.imageRoomLabels);
            }

            if (data.curbAppeal) {
                setCurbAppeals(data.curbAppeal);
            }

            if (data.ceilings) {
                setCeilings(data.ceilings);
            }

            if (data.floorZPID) {
                setFloorZPID(data.floorZPID);
            }

            if (data.ceilingZPID) {
                setCeilingZPID(data.ceilingZPID);
            }

            if (data.assumptions) {
                setAssumptions(data.assumptions);
            }

            if (data.basementAnalysis) {
                setBasementAnalysis(data.basementAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.basement) {
                        return { ...prev, basement: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.floodZoneAnalysis) {
                setFloodZoneAnalysis(data.floodZoneAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.floodZone) {
                        return { ...prev, floodZone: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.fullSubdivisionAnalysis) {
                setFullSubdivisionAnalysis(data.fullSubdivisionAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.subdivision) {
                        return { ...prev, subdivision: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.redfinMarketTrends && data.redfinMarketTrends.analysis) {
                setRedfinMarketTrends(data.redfinMarketTrends);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.redfin) {
                        return { ...prev, redfin: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.constructionMaterialAnalysis) {
                setConstructionMaterialAnalysis(data.constructionMaterialAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.construction) {
                        return { ...prev, construction: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.remodelLevel) {
                setRemodelLevel(data.remodelLevel);
            }

            if (data.subjectPropertyAnalysis) {
                setSubjectPropertyAnalysis(data.subjectPropertyAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.subjectProperty) {
                        return { ...prev, subjectProperty: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            // Update arvAgent progressive fields (market temp, markets, phase)
            if (data.arvAgentPhase && setArvAgentPhase) {
                setArvAgentPhase(data.arvAgentPhase);
            }
            if (data.date && setLastRunAt) {
                const d = data.date;
                const iso = d?.toDate ? d.toDate().toISOString() : (d?.seconds ? new Date(d.seconds * 1000).toISOString() : d);
                setLastRunAt(iso);
            }
            if (data.arvAgentMarketTemp && setArvAgentMarketTemp) {
                setArvAgentMarketTemp(data.arvAgentMarketTemp);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.marketTemp) {
                        return { ...prev, marketTemp: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }
            if (data.compFetcherV1FilterReport !== undefined && setCompFetcherFilterReport) {
                setCompFetcherFilterReport(data.compFetcherV1FilterReport || null);
            }
            if (data.arvAgentMarkets && setArvAgentMarkets) {
                // arvAgentMarkets is the single source of truth for new runs —
                // written post-Stage-2 with the verified comp set, with the
                // pre-expansion Stage 1 snapshot living on arv_traces only.
                //
                // Backwards compat: leads run before this change have
                // arvAgentMarkets = Stage 1 snapshot and arvAgentMarketsProcessed
                // = post-Stage-2. Prefer the legacy "Processed" mirror for those
                // so they don't regress to showing pre-verification comps.
                // The mirror is removed on every fresh run (runExecution.js
                // pre-clear deletes the field), so this branch only fires for
                // old leads that haven't been re-run yet.
                const legacyProcessed = data.arvAgentMarketsProcessed?.markets;
                const sourceMarkets = legacyProcessed || data.arvAgentMarkets.markets || [];
                const sorted = [...sourceMarkets].sort((a, b) => {
                    return (a.isSubjectMarket ? 0 : 1) - (b.isSubjectMarket ? 0 : 1);
                });
                setArvAgentMarkets({
                    ...data.arvAgentMarkets,
                    markets: sorted,
                    marketCount: sorted.length,
                });
                setAnalysisArrivalOrder(prev => {
                    if (!prev.markets) {
                        return { ...prev, markets: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            // Update repair costs if they exist (real-time updates via listener)
            if (data.repairCost) {
                setRepairCostsData({ data: data.repairCost });
                // Mark as fetched if not already marked (for recently_viewed tracking)
                if (!repairCostsFetchedRef.current) {
                    repairCostsFetchedRef.current = true;
                    setRepairCostsFetched(true);
                }
            }

            if (data.propertyEstimates) {
                setPropertyEstimates(data.propertyEstimates);
            }

            if (data.sourceURL) {
                setSourceURL(data.sourceURL);
            }

            if (data.source) {
                setSource(data.source);
            }

            // For external ARV reports, populate arvEstimate and profit from properties doc
            if (data.arvSource === 'external' && data.arvData?.arv) {
                setArvEstimate(data.arvData.arv);
            }

            setTypeOutText(true);
        });

        // Kick off the long‑running Cloud Function without waiting for its HTTP
        // response. We rely on the Firestore listener above for progress/results.
        const getSettings = await fetchSettings(newCompanyId);
        
        if (countyId === "04013") {
            // If we're in Maricopa county, make these settings "nice-to-have"
            // 1. Pool
            // 2. Construction Materials
            getSettings.pool = false;
            getSettings.constructionMaterials = false;
        }

        if (!skipAgent) {
            startCompSelectionAgent(
                newAllComps,
                newProperty,
                newCompanyId,
                newProperty.uniqueIds.propertyId,
                newPropertyNotes,
                newADU,
                newPool,
                newGarage,
                newGarageSpaces,
                newGarageType,
                getSettings,
                false,
                null,
                newRemodelLevel,
                newFirstName,
                refreshSubjectAnalysis
            ).catch(err => {
                // Prevent an unhandled‑promise rejection if the browser drops
                // the connection before the function responds.
                if (process.env.NODE_ENV === 'development') {
                    console.error("startCompSelectionAgent failed:", err);
                }
            });
        }

        // Return unsubscribe function for cleanup
        return unsubscribe;
    }, [
        db,
        countyId,
        fetchSettings,
        pickSelectedCompConditions,
        arvMessagesLengthRef,
        setSelectedComps,
        setStreetViewAnalysis,
        setArvMessages,
        setTypeOutText,
        setRedfinData,
        setArvRequested,
        setArvEstimate,
        setArvRange,
        setRiskSpectrum,
        setArvModeActive,
        setArvReasoning,
        setPurchasePrice,
        setSubdivisions,
        setConditionUpdate,
        setImageRoomLabels,
        setCurbAppeals,
        setCeilings,
        setFloorZPID,
        setCeilingZPID,
        setAssumptions,
        setBasementAnalysis,
        setFloodZoneAnalysis,
        setFullSubdivisionAnalysis,
        setRedfinMarketTrends,
        setConstructionMaterialAnalysis,
        setSubjectPropertyAnalysis,
        setAnalysisArrivalOrder,
        setRepairCostsData,
        setRepairCostsFetched,
        repairCostsFetchedRef,
        setPropertyEstimates,
        setSourceURL,
        setSource,
        setRemodelLevel,
        setArvAgentMarketTemp,
        setArvAgentMarkets,
        setArvAgentPhase,
        setCrossMarketBounds,
        setArvConfidence,
        setArvAgentCrossMarketWidening,
        setArvAgentInvestigationSummary,
        setArvJourney,
        setArvPhases,
        setCompsAnalysis,
        setLastRunAt,
        setCompProblemCodes,
        setCompScoreDetails,
        setAsIsData,
        setRentReadyData,
        setCompFetcherFilterReport,
        skipAgent
    ]);

    const querySavedProperty = useCallback(async(newCompanyId, newProperty, newAllComps, newFirstName, navigate, refreshSubjectAnalysis = false) => {
        const colRef = "companies";
        const docRef = newCompanyId;
        const subColRef = "properties";
        const subDocRef = newProperty.uniqueIds.propertyId;

        const queryData = await getSubDocument(colRef, docRef, subColRef, subDocRef);
        if ( queryData.status === 200 ) {
            let agentInitiated = false;
            const data = queryData.data.data;

            // Resolve verified characteristics for amenity presence. The
            // authoritative enrichment (garage/pool/spaces/type) lives on the
            // LEAD doc's verifiedCharacteristics; in non-disclosure markets
            // (MO/KS/TX) the AWS subject + property doc often lack the garage
            // flag, so the subject would otherwise be scored garage-less and
            // garage comps penalized. Prefer it from the property doc (backend
            // now persists it); if absent (legacy docs) best-effort fetch the
            // lead.
            //
            // Precedence = OR-semantics: an amenity is present if EITHER the
            // property doc (user/AWS) OR the verified enrichment says true. This
            // fixes the common bug (the doc defaulted `garage:false`, enrichment
            // says true) WITHOUT overriding a user's explicit "yes", and matches
            // the backend's "garage present" outcome on re-run. We deliberately
            // do NOT let a verified `false` flip a doc `true` off — that's the
            // user-override direction the backend (applyPropertyOverrides) wins.
            let verifiedChars = data.verifiedCharacteristics || null;
            if ( !verifiedChars && process.env.REACT_APP_GET_LEAD_DETAIL ) {
                try {
                    const leadRes = await cloudFunctionV2(process.env.REACT_APP_GET_LEAD_DETAIL, {
                        companyId: newCompanyId,
                        // String() — leads store data.propertyId as a string and the
                        // getLeadDetail WHERE clause is type-strict (no coercion).
                        propertyId: String(subDocRef),
                        leadFields: ['verifiedCharacteristics']
                    });
                    verifiedChars = leadRes?.lead?.verifiedCharacteristics || null;
                } catch ( err ) {
                    // Best-effort only — fall back to property-doc values.
                }
            }
            const vc = verifiedChars || {};

            const newADU = data.adu === undefined ? false : data.adu;
            if ( newADU ) {
                setAdu(true);
            }

            // Pool: hydrate ONLY from the override sources (Firestore data.pool
            // and verifiedCharacteristics.pool). Do NOT OR in the AWS subject
            // value — `pool` state is written back to Firestore by
            // reRunReport's propertyData.property() template, so leaking AWS in
            // here lets a transient AWS pool=true permanently corrupt
            // data.pool. Once data.pool=true is written, applyPropertyOverrides
            // in the arvAgent preprocessor will keep stomping AWS pool=false
            // on every subsequent run.
            //
            // Garage keeps the AWS OR below — in non-disclosure markets
            // (MO/KS/TX) the AWS subject is the only source for garage, and
            // re-runs must send the same garage the auto-ingestion ARV used.
            const newPool = data.pool === true || vc.pool === true;
            setPool(newPool);

            const newGarage = data.garage === true || vc.garage === true
                || newProperty?.propertyAttributes?.garage === true;
            if ( newGarage ) {
                setGarage(true);
            }

            let newGarageSpaces = null;
            const vcGarageSpaces = Number(vc.garageSpaces);
            if ( newGarage && Number.isFinite(vcGarageSpaces) && vcGarageSpaces > 0 ) {
                setGarageSpaces(vcGarageSpaces);
                newGarageSpaces = vcGarageSpaces;
            // 🪤 `newGarage &&` GUARDS THE FALLBACK TOO. This branch is the route a user-set count
            // takes when verifiedCharacteristics has no number of its own, so it must stay — but
            // unguarded it also fires when we have just concluded there is NO garage, loading a
            // bay count beside `garage: false`. That state is handed to comp re-runs
            // (src/functions/comps/index.js:390 re-guards, so it was latent rather than visible).
            // Measured 2026-09-08: 58 stored docs carried exactly that contradiction.
            } else if ( newGarage && data.garageSpaces !== undefined && data.garageSpaces !== null ) {
                const spaces = Number(data.garageSpaces);
                if ( !isNaN(spaces) && spaces > 0 ) {
                    setGarageSpaces(spaces);
                    newGarageSpaces = spaces;
                }
            }

            let newGarageType = null;
            if ( newGarage && ( vc.garageType === "attached" || vc.garageType === "detached" ) ) {
                setGarageType(vc.garageType);
                newGarageType = vc.garageType;
            } else if ( newGarage && ( data.garageType === "attached" || data.garageType === "detached" ) ) {
                setGarageType(data.garageType);
                newGarageType = data.garageType;
            }

            const newCarport = data.carport === undefined ? false : data.carport;
            if ( newCarport ) {
                setCarport(true);
            }

            if ( data.carportSpaces !== undefined && data.carportSpaces !== null ) {
                const spaces = Number(data.carportSpaces);
                if ( !isNaN(spaces) && spaces > 0 ) {
                    setCarportSpaces(spaces);
                }
            }

            const newBasement = data.basement === undefined ? false : data.basement;
            if ( newBasement ) {
                setBasement(true);
            }
            else if ( data.basementAnalysis?.subjectBasement ) {
                setBasement(true);
            }
            // arvAgent's image-classifier / image-LLM verdict, persisted by
            // postProcessor at arvData.subjectFeatures.basement. This is the
            // ONLY basement signal on auto-ingested leads (data.basement is
            // only written by VerifyProperty, per functions/AGENTS.md), so
            // without it a vision-detected basement disappears from the
            // PropertyCard until a user manually flags it.
            else if ( data.arvData?.subjectFeatures?.basement === true ) {
                setBasement(true);
            }

            if ( data.basementSqFootage !== undefined && data.basementSqFootage !== null ) {
                const sqft = Number(data.basementSqFootage);
                if ( !isNaN(sqft) && sqft >= 0 ) {
                    setBasementSqFootage(sqft);
                }
            }

            if ( data.ceilings ) {
                setCeilings(data.ceilings);
            }

            if ( data.minLotSize ) {
                setMinLotSize(data.minLotSize);
            }

            const newRemodelLevel = data.remodelLevel === undefined ? 1 : data.remodelLevel;
            setRemodelLevel(newRemodelLevel);

            const newPropertyNotes = data.propertyNotes === undefined ? "" : data.propertyNotes;
            setPropertyNotes(newPropertyNotes);

            // Update lot size if reAPILotSize exists - do this BEFORE conditional logic
            // to ensure it's always applied regardless of selectedComps or arvMessages state
            if ( data.reAPILotSize ) {
                newProperty = {
                    ...newProperty,
                    propertyAttributes: {
                        ...newProperty.propertyAttributes,
                        lotSquareFootage: data.reAPILotSize
                    }
                };
                setProperty(newProperty);
            }

            if (data.selectedComps !== undefined && data.selectedComps.length > 0) {
               setSelectedComps(data.selectedComps);
               pickSelectedCompConditions(data.selectedComps, newAllComps);
            }

            // Mirror of the queryBestComps branch above — needed because the
            // initial fetch path can land us here before the snapshot listener
            // fires.
            if (data.rentReadyData !== undefined) {
                setRentReadyData?.(data.rentReadyData || null);
            }
            if (data.asIsData !== undefined) {
                setAsIsData?.(data.asIsData || null);
            }
            // Set up Firestore listener: always when skipAgent (need it for progressive writes),
            // or when no selectedComps exist (normal flow triggers agent + listener)
            if (!data.selectedComps?.length || skipAgent) {
                if (!agentInitiated) {
                    const unsubscribe = await queryBestComps(newProperty, newAllComps, newCompanyId, newPropertyNotes, newADU, newPool, newGarage, newGarageSpaces, newGarageType, newRemodelLevel, newFirstName, refreshSubjectAnalysis);
                    if (unsubscribe) {
                        firestoreUnsubscribeRef.current = unsubscribe;
                    }
                    agentInitiated = true;
                }
            }

            if ( data.subdivisions ) {
                setSubdivisions(data.subdivisions);
            }

            if ( data.streetViewAnalysis !== undefined ) {
                setStreetViewAnalysis(data.streetViewAnalysis);
            }

            if ( data.anomaly ) {
                setAnomaly(data.anomaly);
            }

            if ( data.conditionUpdate ) {
                setConditionUpdate(data.conditionUpdate);
            }

            if ( data.imageRoomLabels ) {
                setImageRoomLabels(data.imageRoomLabels);
            }

            if ( data.curbAppeal ) {
                setCurbAppeals(data.curbAppeal);
            }

            if ( data.floorZPID ) {
                setFloorZPID(data.floorZPID);
            }

            if ( data.ceilingZPID ) {
                setCeilingZPID(data.ceilingZPID);
            }

            if ( data.assumptions ) {
                setAssumptions(data.assumptions);
            }

            if (data.redfinData) {
                setRedfinData(data.redfinData);
            }

            if ( data.basementAnalysis ) {
                setBasementAnalysis(data.basementAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.basement) {
                        return { ...prev, basement: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.floodZoneAnalysis) {
                setFloodZoneAnalysis(data.floodZoneAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.floodZone) {
                        return { ...prev, floodZone: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.fullSubdivisionAnalysis) {
                setFullSubdivisionAnalysis(data.fullSubdivisionAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.subdivision) {
                        return { ...prev, subdivision: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.redfinMarketTrends && data.redfinMarketTrends.analysis) {
                setRedfinMarketTrends(data.redfinMarketTrends);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.redfin) {
                        return { ...prev, redfin: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.constructionMaterialAnalysis) {
                setConstructionMaterialAnalysis(data.constructionMaterialAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.construction) {
                        return { ...prev, construction: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.subjectPropertyAnalysis) {
                setSubjectPropertyAnalysis(data.subjectPropertyAnalysis);
                setAnalysisArrivalOrder(prev => {
                    if (!prev.subjectProperty) {
                        return { ...prev, subjectProperty: { arrivalTime: Date.now() } };
                    }
                    return prev;
                });
            }

            if (data.propertyEstimates) {
                setPropertyEstimates(data.propertyEstimates);
            }

            if (data.sourceURL) {
                setSourceURL(data.sourceURL);
            }

            if (data.source) {
                setSource(data.source);
            }

            if ( data.arvRequested === false ) {
                // Don't show ARV yet as they haven't requested it yet
                setArvRequested(false);
            }
            else if ( data.arvData !== undefined ) {
                setArvRequested(true);
                if ( data.arvData.arv !== undefined && data.arvData.arv !== null ) {
                    setArvEstimate(data.arvData.arv);
                    setArvRange(data.arvData.arvRange);
                    if ( data.arvData.riskSpectrum !== undefined && data.arvData.riskSpectrum !== null ) {
                        setRiskSpectrum(data.arvData.riskSpectrum);
                    }
                    setArvModeActive(true);
                    setArvReasoning(data.arvData.reasoning);
                }
            }

            // Handle repair costs data
            if (data.repairCost) {
                setRepairCostsData({ data: data.repairCost });
                if (!repairCostsFetchedRef.current) {
                    repairCostsFetchedRef.current = true;
                    setRepairCostsFetched(true);
                }
                
                // Auto-open repair costs if no ARV exists
                const hasARV = data.arvData?.arv !== null && data.arvData?.arv !== undefined;
                if (!hasARV) {
                    setViewRepairCosts(true);
                }
            }

            // Mark repair costs as fetched even if none exist (for recently_viewed tracking)
            if (!repairCostsFetchedRef.current) {
                repairCostsFetchedRef.current = true;
                setRepairCostsFetched(true);
            }

            if ( data.purchasePrice ) {
                setPurchasePrice(data.purchasePrice);
            }

            if ( data.arvWorkflow ) {
                setArvWorkflow(data.arvWorkflow);
            }

            if ( data.arvMessages !== undefined ) {
                setArvMessages(data.arvMessages);
            }
            else {
                // Store unsubscribe function for cleanup
                if (!agentInitiated) {
                    const unsubscribe = await queryBestComps(newProperty, newAllComps, newCompanyId, newPropertyNotes, newADU, newPool, newGarage, newGarageSpaces, newGarageType, newRemodelLevel, newFirstName, refreshSubjectAnalysis);
                    if (unsubscribe) {
                        firestoreUnsubscribeRef.current = unsubscribe;
                    }
                    agentInitiated = true;
                }
            }
            
            setLoading(false);
        }
        else {
            navigate("/");
        }
    }, [
        queryBestComps,
        pickSelectedCompConditions,
        firestoreUnsubscribeRef,
        setAdu,
        setPool,
        setGarage,
        setGarageSpaces,
        setGarageType,
        setCarport,
        setCarportSpaces,
        setBasement,
        setCeilings,
        setMinLotSize,
        setRemodelLevel,
        setPropertyNotes,
        setSelectedComps,
        setSubdivisions,
        setStreetViewAnalysis,
        setConditionUpdate,
        setImageRoomLabels,
        setCurbAppeals,
        setFloorZPID,
        setCeilingZPID,
        setAssumptions,
        setRedfinData,
        setBasementAnalysis,
        setFloodZoneAnalysis,
        setFullSubdivisionAnalysis,
        setRedfinMarketTrends,
        setConstructionMaterialAnalysis,
        setSubjectPropertyAnalysis,
        setAnalysisArrivalOrder,
        setArvRequested,
        setArvEstimate,
        setArvRange,
        setRiskSpectrum,
        setArvModeActive,
        setArvReasoning,
        setPurchasePrice,
        setArvMessages,
        setLoading,
        setAnomaly,
        setArvWorkflow,
        setPropertyEstimates,
        setSourceURL,
        setSource,
        setBasementSqFootage,
        setProperty,
        setRepairCostsData,
        setRepairCostsFetched,
        repairCostsFetchedRef,
        setViewRepairCosts,
        setAsIsData,
        setRentReadyData,
        skipAgent
    ]);

    const fetchPropertyData = useCallback(async(savedUserId, savedCompanyId, newFirstName, navigate, opts = {}) => {
        const forceRun = opts.forceRun === true;
        const refreshSubjectAnalysis = opts.refreshSubjectAnalysis === true;
        const gatewayURL = process.env.REACT_APP_AWS_QUERY_URL;
        const resourceId = "propertyDetailV2";
        const propertyParams = {
            propertyId: String(propertyId),
            userId: savedUserId,
            countyId: String(countyId)
        }

        // Use direct Lambda invocation by default (bypasses API Gateway 30s timeout)
        // Pass ?lambdaDirect=false to use the old gateway path
        const urlParams = new URLSearchParams(window.location.search);
        const useLambdaDirect = urlParams.get('lambdaDirect') !== 'false';

        // Snapshot-first (view path only): compFetcherV1-era reports have their
        // comp set frozen to GCS. Serve it instead of re-pulling the live,
        // non-deterministic data. Skipped on forceRun (a re-run must re-pull/
        // re-compute).
        //
        // V1 leads must NEVER fall back to the AWS gateway — AWS data and V1
        // data disagree per-field (e.g. compDetails.propertyCondition) and
        // mixing them produces silent inconsistencies between the ARV math
        // (V1) and the UI display (AWS). Detection: a lead "ran on V1" if
        // either compsSnapshot.source === 'compFetcherV1' OR
        // compFetcherV1FilterReport is present on the property doc. The
        // FilterReport catches a transitional state where V1 ran but the
        // snapshot write was missed (older runs before the writer was added,
        // or a snapshot failure swallowed by the non-fatal try/catch).
        let getGateway = null;
        let ranOnV1 = false;
        if (!forceRun && process.env.REACT_APP_GET_STORED_COMPS_URL) {
            try {
                const propDoc = await getSubDocument('companies', savedCompanyId, 'properties', String(propertyId));
                const propData = propDoc?.status === 200 ? propDoc?.data?.data : null;
                const compsSnapshot = propData?.arvData?.compsSnapshot;
                const compFetcherV1FilterReport = propData?.compFetcherV1FilterReport;
                ranOnV1 = compsSnapshot?.source === 'compFetcherV1' || !!compFetcherV1FilterReport;
                if (compsSnapshot?.source === 'compFetcherV1') {
                    const snapRes = await cloudFunctionV2(
                        process.env.REACT_APP_GET_STORED_COMPS_URL,
                        { companyId: savedCompanyId, propertyId: String(propertyId) }
                    );
                    if (snapRes?.status === 200) getGateway = snapRes;
                    else console.warn(`[usePropertyData] stored-comps returned ${snapRes?.status} for V1 lead`);
                }
            } catch (snapErr) {
                console.warn('[usePropertyData] snapshot check failed:', snapErr?.message);
            }
        }

        // V1 ran but no snapshot — refuse to fall back to AWS. Surface an
        // error so the user can re-run the report (which will write the
        // snapshot via arvAgent.js). Silently calling AWS here is the bug
        // that produced V1-math/AWS-labels mismatches (e.g. subject 21306367).
        if (ranOnV1 && !getGateway) {
            console.error('[usePropertyData] V1 ran but snapshot is unavailable — refusing AWS fallback. Re-run the report to regenerate the snapshot.');
            setErrorMessage('This lead’s comp snapshot is unavailable. Please re-run the report to regenerate it.');
            setNetworkError(true);
            setLoading(false);
            return;
        }

        if (!getGateway) {
            let getGatewayParams;
            if (useLambdaDirect) {
                getGatewayParams = {
                    type: "lambdaDirect",
                    testMode: false,
                    lambdaParams: {
                        ...propertyParams,
                        forceRun: forceRun
                    }
                };
            } else {
                console.log("[FALLBACK] Using gateway path");
                const builder = forceRun ? propertyGatewayStringReRun : propertyGatewayString;
                const getGatewayQueryString = await builder("propertyDetailv2?", propertyParams);
                getGatewayParams = {
                    type: "gateway",
                    resourceId: resourceId,
                    queryString: getGatewayQueryString
                };
            }
            getGateway = await cloudFunctionV2(gatewayURL, getGatewayParams);
        }
        if ( getGateway.status === 200 ) {
            const body = getGateway.body;
            const data = body.data || body;
            if (body.error !== undefined) {
                setErrorMessage(`Error: ${body.error}`);
                setNetworkError(true);
                setLoading(false);
                return;
            }

            document.title = `${data.propertyDetails.addressDetails.streetAddress}, ${data.propertyDetails.addressDetails.city}, ${data.propertyDetails.addressDetails.state} ${data.propertyDetails.addressDetails.postalCode} | Clozers`;
            
            const salesComparables = data.salesComparables.comps;
            const recentlySold = salesComparables.recentlySold;
            const forSale = salesComparables.forSale;
            const fullList = recentlySold.concat(forSale);

            const amendedFullList = [];
            for (let index = 0; index < fullList.length; index++) {
                const element = fullList[index].propertyDetails;
                amendedFullList.push(element);
            }
            amendedFullList.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
            
            const mlsAdjustedList = await getMLSData(savedCompanyId, amendedFullList);
            
            setAllComps(mlsAdjustedList);
            await regenerateTimeline(mlsAdjustedList);
            setFilteredComps(mlsAdjustedList);

            if ( savedCompanyId !== null ) {
                querySavedProperty(savedCompanyId, data.propertyDetails, mlsAdjustedList, newFirstName, navigate, refreshSubjectAnalysis);
            }
            else {
                setLoading(false);
            }
            setProperty(data.propertyDetails);
            setNetworkError(false);
        }
        else if (getGateway.status === 405) {
            setErrorMessage("Internal Server Error. Please try again later.");
            setNetworkError(true);
            setLoading(false);
            await sendErrorEmail({
                userId: savedUserId,
                address: "",
                notes: "Comps Results page",
                propertyId: propertyId
            });
        }
        else if ( getGateway.status === 406 ) {
            setNetworkError(false);
            setLoading(false);
        }
        else {
            setNetworkError(false);
            
            // Check Firebase for repair costs if API call failed
            if (savedCompanyId && propertyId) {
                try {
                    const colRef = "companies";
                    const docRef = savedCompanyId;
                    const subColRef = "properties";
                    const subDocRef = propertyId;
                    
                    const queryData = await getSubDocument(colRef, docRef, subColRef, subDocRef);
                    if (queryData.status === 200) {
                        const data = queryData.data.data;

                        // Update page title from Firebase property address in fallback mode
                        const fallbackAddress = data?.address || null;
                        if (fallbackAddress?.streetAddress) {
                            const city = fallbackAddress.city || "";
                            const state = fallbackAddress.state || "";
                            const zipcode = fallbackAddress.zipcode || fallbackAddress.postalCode || "";
                            const titlePrefix = [fallbackAddress.streetAddress, city].filter(Boolean).join(", ");
                            const stateZip = `${state} ${zipcode}`.trim();
                            const titleAddress = [titlePrefix, stateZip].filter(Boolean).join(", ");
                            document.title = `${titleAddress} | Clozers`;
                        }

                        if (data.repairCost) {
                            const hasARV = data.arvData?.arv !== null && data.arvData?.arv !== undefined;
                            if (!hasARV) {
                                setRepairCostsData({ data: data.repairCost });
                                repairCostsFetchedRef.current = true;
                                setRepairCostsFetched(true);
                                setViewRepairCosts(true);
                            }
                        }
                    }
                } catch (error) {
                    // Silently fail - property load already failed
                    if (process.env.NODE_ENV === 'development') {
                        console.error("Error checking Firebase for repair costs:", error);
                    }
                }
            }
            
            setLoading(false);
        }
    }, [
        propertyId,
        countyId,
        getMLSData,
        querySavedProperty,
        setAllComps,
        setFilteredComps,
        setProperty,
        setLoading,
        setErrorMessage,
        setNetworkError,
        setRepairCostsData,
        setRepairCostsFetched,
        repairCostsFetchedRef,
        setViewRepairCosts
    ]);

    return {
        fetchPropertyData,
        getMLSData,
        fetchSettings,
        pickSelectedCompConditions,
        queryBestComps,
        querySavedProperty
    };
};

